import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { LookupTypeApi } from '../api/lookup-type-api';
import { LookupType } from '../models/lookup/lookup-type';
import { Country, CountryType, State } from '../models/address/country-type';
import { EventRegistrationTypeId } from '../models/shared/championship-and-event.enum';

@Injectable({
  providedIn: 'root',
})
export class LookupTypeRepository {
  private lookupTypeApi = inject(LookupTypeApi);

  // Cached observables for lookup values
  private memberStatusTypes$?: Observable<LookupType[]>;
  private zoneTypes$?: Observable<LookupType[]>;
  private zoneTypesUnsorted$?: Observable<LookupType[]>;
  private zoneTypesIncludingNational$?: Observable<LookupType[]>;
  private jobCategoryTypes$?: Observable<LookupType[]>;
  private genderTypes$?: Observable<LookupType[]>;
  private languageTypes$?: Observable<LookupType[]>;
  private memberTypes$?: Observable<LookupType[]>;
  private countryTypes$?: Observable<Country[]>;
  private allCountryTypes$?: Observable<Country[]>;
  private applicationStatusTypes$?: Observable<LookupType[]>;
  private applicationTaskStatusTypes$?: Observable<LookupType[]>;
  private facilityTypes$?: Observable<LookupType[]>;
  private facilityStatusTypes$?: Observable<LookupType[]>;
  private golfCourseTypes$?: Observable<LookupType[]>;
  private raceTypes$?: Observable<LookupType[]>;
  private ethnicityTypes$?: Observable<LookupType[]>;
  private pronounTypes$?: Observable<LookupType[]>;
  private lgbtStatusTypes$?: Observable<LookupType[]>;
  private indigenousStatusTypes$?: Observable<LookupType[]>;
  private indigenousTypes$?: Observable<LookupType[]>;
  private disabilityStatusTypes$?: Observable<LookupType[]>;
  private disabilityTypes$?: Observable<LookupType[]>;
  private memberEmployerStatusTypes$?: Observable<LookupType[]>;
  private eventStatusTypes$?: Observable<LookupType[]>;
  private eventTypes$?: Observable<LookupType[]>;
  private championshipRegistrationTypes$?: Observable<LookupType[]>;
  private eventRegistrationTypes$?: Observable<LookupType[]>;
  private eventRegistrationStatusTypes$?: Observable<LookupType[]>;
  private glAccountTypes$?: Observable<LookupType[]>;
  private invoiceStatusTypes$?: Observable<LookupType[]>;
  private invoiceTypes$?: Observable<LookupType[]>;
  private memberClassificationTypes$?: Observable<LookupType[]>;
  private eventRegistrantTypes$?: Observable<LookupType[]>;
  private alertStatusTypes$?: Observable<LookupType[]>;

  getMemberStatusTypes(): Observable<LookupType[]> {
    if (!this.memberStatusTypes$) {
      this.memberStatusTypes$ = this.lookupTypeApi.getMemberStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
        map(newZoneTypes => {
          newZoneTypes.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
          return newZoneTypes;
        }),
      );
    }
    return this.memberStatusTypes$;
  }

  getCountryTypes(): Observable<Country[]> {
    if (!this.countryTypes$) {
      this.countryTypes$ = this.lookupTypeApi.getCountryTypes(true).pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.countryTypes$;
  }

  getProvinceTypes(): Observable<State[]> {
    if (!this.countryTypes$) {
      this.countryTypes$ = this.lookupTypeApi.getCountryTypes(true).pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.countryTypes$.pipe(
      map(countries => countries.filter(c => c.id === CountryType.Canada).flatMap(country => country.states ?? [])),
    );
  }

  getAllCountryTypes(): Observable<Country[]> {
    if (!this.allCountryTypes$) {
      this.allCountryTypes$ = this.lookupTypeApi.getCountryTypes(false).pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.allCountryTypes$;
  }

  getZoneTypesOrdered(includeNational: boolean = false, sortOrder: number[] = []): Observable<LookupType[]> {
    if (!this.zoneTypesUnsorted$) {
      this.zoneTypesUnsorted$ = this.lookupTypeApi.getZoneTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.zoneTypesUnsorted$.pipe(
      map(zoneTypes => {
        const zones = includeNational
          ? [...zoneTypes]
          : zoneTypes.filter(zoneType => zoneType.id !== ZoneType.National);

        const orderMap = new Map(sortOrder.map((id, index) => [id, index]));
        return [...zones].sort((a, b) => {
          const aRank = orderMap.get(a.id ?? -1) ?? Number.MAX_SAFE_INTEGER;
          const bRank = orderMap.get(b.id ?? -1) ?? Number.MAX_SAFE_INTEGER;

          if (aRank !== bRank) {
            return aRank - bRank;
          }

          return (a.name ?? '').localeCompare(b.name ?? '');
        });
      }),
    );
  }

  getZoneTypes(): Observable<LookupType[]> {
    if (!this.zoneTypes$) {
      this.zoneTypes$ = this.getZoneTypesIncludingNational().pipe(
        map(value => value.filter(zoneType => zoneType.id !== ZoneType.National)),
      );
    }
    return this.zoneTypes$;
  }

  getZoneTypesIncludingNational(): Observable<LookupType[]> {
    if (!this.zoneTypesIncludingNational$) {
      this.zoneTypesIncludingNational$ = this.lookupTypeApi.getZoneTypes().pipe(
        map(o => o.value),
        shareReplay(1),
        map(newZoneTypes => {
          newZoneTypes.sort((a, b) => (a.id === ZoneType.National ? 1 : (a.name ?? '').localeCompare(b.name ?? '')));
          return newZoneTypes;
        }),
      );
    }
    return this.zoneTypesIncludingNational$;
  }

  getJobCategoryTypes(): Observable<LookupType[]> {
    if (!this.jobCategoryTypes$) {
      this.jobCategoryTypes$ = this.lookupTypeApi.getJobCategoryTypes().pipe(
        map(o => o.value),
        shareReplay(1),
        map(newZoneTypes => {
          newZoneTypes.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
          return newZoneTypes;
        }),
      );
    }
    return this.jobCategoryTypes$;
  }

  getGenderTypes(): Observable<LookupType[]> {
    if (!this.genderTypes$) {
      this.genderTypes$ = this.lookupTypeApi.getGenderTypes().pipe(
        map(o => o.value),
        shareReplay(1),
        map(newZoneTypes => {
          newZoneTypes.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
          return newZoneTypes;
        }),
      );
    }
    return this.genderTypes$;
  }

  getLanguageTypes(): Observable<LookupType[]> {
    if (!this.languageTypes$) {
      this.languageTypes$ = this.lookupTypeApi.getLanguageTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.languageTypes$;
  }

  getMemberTypes(): Observable<LookupType[]> {
    if (!this.memberTypes$) {
      this.memberTypes$ = this.lookupTypeApi.getMemberTypes().pipe(
        map(o => o.value),
        shareReplay(1),
        map(newZoneTypes => {
          newZoneTypes.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
          return newZoneTypes;
        }),
      );
    }
    return this.memberTypes$;
  }

  getMemberEmployerStatusTypes(): Observable<LookupType[]> {
    if (!this.memberEmployerStatusTypes$) {
      this.memberEmployerStatusTypes$ = this.lookupTypeApi.getMemberEmployerStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.memberEmployerStatusTypes$;
  }

  getApplicationStatusTypes(): Observable<LookupType[]> {
    if (!this.applicationStatusTypes$) {
      this.applicationStatusTypes$ = this.lookupTypeApi.getApplicationStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.applicationStatusTypes$;
  }

  getApplicationTaskStatusTypes(): Observable<LookupType[]> {
    if (!this.applicationTaskStatusTypes$) {
      this.applicationTaskStatusTypes$ = this.lookupTypeApi.getApplicationTaskStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.applicationTaskStatusTypes$;
  }

  getFacilityTypes(): Observable<LookupType[]> {
    if (!this.facilityTypes$) {
      this.facilityTypes$ = this.lookupTypeApi.getFacilityTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.facilityTypes$;
  }

  getFacilityStatusTypes(): Observable<LookupType[]> {
    if (!this.facilityStatusTypes$) {
      this.facilityStatusTypes$ = this.lookupTypeApi.getFacilityStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.facilityStatusTypes$;
  }

  getGolfCourseTypes(): Observable<LookupType[]> {
    if (!this.golfCourseTypes$) {
      this.golfCourseTypes$ = this.lookupTypeApi.getGolfCourseTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.golfCourseTypes$;
  }

  getRaceTypes(): Observable<LookupType[]> {
    if (!this.raceTypes$) {
      this.raceTypes$ = this.lookupTypeApi.getRaceTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.raceTypes$;
  }

  getEthnicityTypes(): Observable<LookupType[]> {
    if (!this.ethnicityTypes$) {
      this.ethnicityTypes$ = this.lookupTypeApi.getEthnicityTypes().pipe(
        map(o => o.value),
        map(lookupTypes => {
          const [valuesWhoShouldntBeSorted, valuesWhoShouldBeSorted]: [LookupType[], LookupType[]] = lookupTypes.reduce<
            [LookupType[], LookupType[]]
          >(
            ([unsorted, sorted], lt) => {
              if (
                [EthnicityType.MixedOrigin, EthnicityType.PreferNotToAnswer, EthnicityType.Other].includes(lt.id ?? 0)
              ) {
                unsorted.push(lt);
              } else {
                sorted.push(lt);
              }
              return [unsorted, sorted];
            },
            [[], []],
          );

          valuesWhoShouldBeSorted.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
          return [...valuesWhoShouldBeSorted, ...valuesWhoShouldntBeSorted];
        }),
        shareReplay(1),
      );
    }
    return this.ethnicityTypes$;
  }

  getPronounTypes(): Observable<LookupType[]> {
    if (!this.pronounTypes$) {
      this.pronounTypes$ = this.lookupTypeApi.getPronounTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.pronounTypes$;
  }

  getLgbtStatusTypes(): Observable<LookupType[]> {
    if (!this.lgbtStatusTypes$) {
      this.lgbtStatusTypes$ = this.lookupTypeApi.getLgbtStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.lgbtStatusTypes$;
  }

  getIndigenousStatusTypes(): Observable<LookupType[]> {
    if (!this.indigenousStatusTypes$) {
      this.indigenousStatusTypes$ = this.lookupTypeApi.getIndigenousStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.indigenousStatusTypes$;
  }

  getIndigenousTypes(): Observable<LookupType[]> {
    if (!this.indigenousTypes$) {
      this.indigenousTypes$ = this.lookupTypeApi.getIndigenousTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.indigenousTypes$;
  }

  getDisabilityStatusTypes(): Observable<LookupType[]> {
    if (!this.disabilityStatusTypes$) {
      this.disabilityStatusTypes$ = this.lookupTypeApi.getDisabilityStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.disabilityStatusTypes$;
  }

  getDisabilityTypes(): Observable<LookupType[]> {
    if (!this.disabilityTypes$) {
      this.disabilityTypes$ = this.lookupTypeApi.getDisabilityTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.disabilityTypes$;
  }

  getEventStatusTypes(): Observable<LookupType[]> {
    if (!this.eventStatusTypes$) {
      this.eventStatusTypes$ = this.lookupTypeApi.getEventStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.eventStatusTypes$;
  }

  getEventTypes(): Observable<LookupType[]> {
    if (!this.eventTypes$) {
      this.eventTypes$ = this.lookupTypeApi.getEventTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.eventTypes$;
  }

  getChampionshipRegistrationTypes(): Observable<LookupType[]> {
    if (!this.championshipRegistrationTypes$) {
      this.championshipRegistrationTypes$ = this.lookupTypeApi.getEventRegistrationTypes().pipe(
        map(o => o.value.filter(registrationType => registrationType.id !== EventRegistrationTypeId.Vendor)),
        shareReplay(1),
      );
    }
    return this.championshipRegistrationTypes$;
  }

  getEventRegistrationTypes(): Observable<LookupType[]> {
    if (!this.eventRegistrationTypes$) {
      this.eventRegistrationTypes$ = this.lookupTypeApi.getEventRegistrationTypes().pipe(
        map(o => o.value.filter(registrationType => registrationType.id !== EventRegistrationTypeId.Team)),
        shareReplay(1),
      );
    }
    return this.eventRegistrationTypes$;
  }

  getEventRegistrationStatusTypes(): Observable<LookupType[]> {
    if (!this.eventRegistrationStatusTypes$) {
      this.eventRegistrationStatusTypes$ = this.lookupTypeApi.getEventRegistrationStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.eventRegistrationStatusTypes$;
  }

  getGlAccountTypes(): Observable<LookupType[]> {
    if (!this.glAccountTypes$) {
      this.glAccountTypes$ = this.lookupTypeApi.getGlAccountTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.glAccountTypes$;
  }

  getInvoiceStatusTypes(): Observable<LookupType[]> {
    if (!this.invoiceStatusTypes$) {
      this.invoiceStatusTypes$ = this.lookupTypeApi.getInvoiceStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.invoiceStatusTypes$;
  }

  getInvoiceTypes(): Observable<LookupType[]> {
    if (!this.invoiceTypes$) {
      this.invoiceTypes$ = this.lookupTypeApi.getInvoiceTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.invoiceTypes$;
  }

  getMemberClassificationTypes$(): Observable<LookupType[]> {
    if (!this.memberClassificationTypes$) {
      this.memberClassificationTypes$ = this.lookupTypeApi.getMemberClassificationTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.memberClassificationTypes$;
  }

  getEventRegistrantTypes(): Observable<LookupType[]> {
    if (!this.eventRegistrantTypes$) {
      this.eventRegistrantTypes$ = this.lookupTypeApi.getEventRegistrantTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.eventRegistrantTypes$;
  }

  getAlertStatusTypes(): Observable<LookupType[]> {
    if (!this.alertStatusTypes$) {
      this.alertStatusTypes$ = this.lookupTypeApi.getAlertStatusTypes().pipe(
        map(o => o.value),
        shareReplay(1),
      );
    }
    return this.alertStatusTypes$;
  }
}

enum EthnicityType {
  MixedOrigin = 20,
  PreferNotToAnswer = 21,
  Other = 22,
}

export enum ZoneType {
  Alberta = 1,
  BritishColumbia,
  Atlantic,
  Ontario,
  NorthernOntario,
  OttawaValley,
  Manitoba,
  Saskatchewan,
  Quebec,
  National,
}
